import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '..'
import { UserApplicationData } from './userManagementSearchService'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

// Constants for User Management filters - matches backend filter categories
export const UserManagementFilterConstants = {
  tools: 'Tools',
  status: 'Status',
  search: 'Search',
} as const

// Interface defining the complete User Management state structure
export interface UserManagementState {
  searchString: string
  toolFilters: string[]
  statusFilters: string[]
  pageNumber: number
  pageSize: RsmPaginationPageSizes
  sortBy: string
  sortDirection: 'asc' | 'desc'
  totalUsers: number
  usersData: UserApplicationData[]
}

// Initial state with default values as specified in requirements
const initialState: UserManagementState = {
  searchString: '',
  toolFilters: [],
  statusFilters: [], // Default filters as specified
  pageNumber: 1,
  pageSize: 10,
  sortBy: 'name', // Default sort by name
  sortDirection: 'asc', // Default ascending sort
  totalUsers: 0,
  usersData: [],
}

// Redux Toolkit slice for User Management state management
export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    // Search string management with page reset
    setSearchString: (state, action: PayloadAction<string>) => {
      // Limit search string to 255 characters as specified
      const searchValue =
        action.payload.length > 255
          ? action.payload.substring(0, 255)
          : action.payload
      state.searchString = searchValue
      state.pageNumber = 1 // Reset to first page when search changes
    },

    // Tool filter management - toggle functionality
    toggleToolFilter: (state, action: PayloadAction<string>) => {
      const tool = action.payload
      const index = state.toolFilters.indexOf(tool)
      if (index === -1) {
        // Add tool if not present
        state.toolFilters.push(tool)
      } else {
        // Remove tool if already present
        state.toolFilters.splice(index, 1)
      }
      state.pageNumber = 1 // Reset to first page when filters change
    },

    // Status filter management - toggle functionality
    toggleStatusFilter: (state, action: PayloadAction<string>) => {
      const status = action.payload
      const index = state.statusFilters.indexOf(status)
      if (index === -1) {
        // Add status if not present
        state.statusFilters.push(status)
      } else {
        // Remove status if already present
        state.statusFilters.splice(index, 1)
      }
      state.pageNumber = 1 // Reset to first page when filters change
    },

    // Replace entire tool filter array
    setToolFilters: (state, action: PayloadAction<string[]>) => {
      state.toolFilters = action.payload
      state.pageNumber = 1 // Reset to first page when filters change
    },

    // Replace entire status filter array
    setStatusFilters: (state, action: PayloadAction<string[]>) => {
      state.statusFilters = action.payload
      state.pageNumber = 1 // Reset to first page when filters change
    },

    // Clear all filters and search, reset to initial state
    clearAllFilters: (state) => {
      state.searchString = initialState.searchString
      state.toolFilters = initialState.toolFilters
      state.statusFilters = initialState.statusFilters
      state.pageNumber = initialState.pageNumber
      state.pageSize = initialState.pageSize
      state.sortBy = initialState.sortBy
      state.sortDirection = initialState.sortDirection
      // Preserve totalUsers as it's not a filter
    },

    // Clear only search string
    clearSearchString: (state) => {
      state.searchString = ''
      state.pageNumber = 1
    },

    // Clear only tool filters
    clearToolFilters: (state) => {
      state.toolFilters = []
      state.pageNumber = 1
    },

    // Clear only status filters
    clearStatusFilters: (state) => {
      state.statusFilters = []
      state.pageNumber = 1
    },

    // Pagination controls
    setPageNumber: (state, action: PayloadAction<number>) => {
      if (action.payload >= 1) {
        state.pageNumber = action.payload
      }
    },

    setPageSize: (state, action: PayloadAction<RsmPaginationPageSizes>) => {
      if (action.payload >= 1) {
        state.pageSize = action.payload
        state.pageNumber = 1 // Reset to first page when page size changes
      }
    },

    // Sorting controls
    setSorting: (
      state,
      action: PayloadAction<{ sortBy: string; sortDirection: 'asc' | 'desc' }>,
    ) => {
      state.sortBy = action.payload.sortBy
      state.sortDirection = action.payload.sortDirection
    },

    // Total user count management
    setTotalUsers: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0) {
        state.totalUsers = action.payload
      }
    },

    // Store complete user dataset from API response
    setUsersData: (state, action: PayloadAction<UserApplicationData[]>) => {
      state.usersData = action.payload
      state.totalUsers = action.payload.length
    },
  },
})

// Export all action creators
export const {
  setSearchString,
  toggleToolFilter,
  toggleStatusFilter,
  setToolFilters,
  setStatusFilters,
  clearAllFilters,
  clearSearchString,
  clearToolFilters,
  clearStatusFilters,
  setPageNumber,
  setPageSize,
  setSorting,
  setTotalUsers,
  setUsersData,
} = userManagementSlice.actions

// Typed selectors for accessing User Management state
export const getUserManagementState = (state: RootState): UserManagementState =>
  state.userManagement

export const getSearchString = (state: RootState): string =>
  state.userManagement.searchString

export const getToolFilters = (state: RootState): string[] =>
  state.userManagement.toolFilters

export const getStatusFilters = (state: RootState): string[] =>
  state.userManagement.statusFilters

// Memoized selector to prevent unnecessary re-renders from new object references
export const getPagination = createSelector(
  [
    (state: RootState) => state.userManagement.pageNumber,
    (state: RootState) => state.userManagement.pageSize,
  ],
  (pageNumber, pageSize) => ({
    pageNumber,
    pageSize,
  }),
)

// Memoized selector to prevent unnecessary re-renders from new object references
export const getSorting = createSelector(
  [
    (state: RootState) => state.userManagement.sortBy,
    (state: RootState) => state.userManagement.sortDirection,
  ],
  (sortBy, sortDirection) => ({
    sortBy,
    sortDirection,
  }),
)

export const getTotalUsers = (state: RootState): number =>
  state.userManagement.totalUsers

export const getUsersData = (state: RootState): UserApplicationData[] =>
  state.userManagement.usersData

// Default export of the reducer for store registration
export default userManagementSlice.reducer
